import type { ClientSummaryDto } from '@fos/shared/contracts/copilot';
import { RefreshCw, Sparkles } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { ErrorState, Skeleton } from '../../../ui/States';
import { TOUCH_BUTTON } from '../../mobile';
import { copilotErrorInfo } from '../copilot-ui';
import { useCopilotInvalidate, useCopilotStatus } from '../hooks';
import { CopilotFootnote } from './CopilotFootnote';

/**
 * „W skrócie” w profilu klienta (Figma 05 Profil REA, kolumna lewa pod „Na naszym serwerze”): 2–3 zdania od AI z datą
 * i źródłami. Ostatnie podsumowanie tej osoby z `copilot.clientSummary`; „Podsumuj” / „Odśwież” = nowe zadanie AI
 * w zakresie osoby (bez sejfu, bez kwot bez „Widzi stawki”). Brak danych o pracy z klientem = jawne „brak danych”, bez
 * dopisywania ustaleń. AI wyłączone = krótka informacja; profil klienta działa bez zmian.
 */
export function ClientSummaryCard({ clientId, className }: { clientId: string; className?: string }) {
  const status = useCopilotStatus();
  const last = trpc.copilot.clientSummary.useQuery({ clientId }, { staleTime: 60_000 });
  const run = trpc.copilot.summarizeClient.useMutation();
  const utils = trpc.useUtils();
  const invalidate = useCopilotInvalidate();
  const [fresh, setFresh] = useState<ClientSummaryDto | null>(null);

  const summary = fresh?.clientId === clientId ? fresh : (last.data ?? null);
  const enabled = status.data?.enabled ?? true;
  const available = status.data?.available ?? true;

  const summarize = async () => {
    try {
      const res = await run.mutateAsync({ clientId });
      setFresh(res);
      if (!res.noData) await utils.copilot.clientSummary.invalidate({ clientId });
      void invalidate.jobs();
    } catch {
      // błąd pod treścią
    }
  };

  const error = run.isError ? copilotErrorInfo(run.error) : null;
  const action =
    enabled && available && !last.isPending && !last.isError ? (
      <Button
        variant="ghost"
        size="s"
        icon={summary ? RefreshCw : Sparkles}
        onClick={() => void summarize()}
        loading={run.isPending}
        className={TOUCH_BUTTON}
      >
        {summary ? 'Odśwież' : 'Podsumuj'}
      </Button>
    ) : undefined;

  let body: ReactNode;
  if (last.isPending) {
    body = (
      <div role="status" aria-label="Wczytywanie podsumowania" className="flex flex-col gap-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-4/5" />
      </div>
    );
  } else if (last.isError) {
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać podsumowania"
        onRetry={() => void last.refetch()}
        retrying={last.isFetching}
      />
    );
  } else if (run.isPending && !summary) {
    body = (
      <p role="status" className="text-body-s text-fg-muted">
        AI pracuje…
      </p>
    );
  } else if (summary) {
    body = (
      <>
        {/* Figma 05 (3026:2774): treść „W skrócie” jak wiersze listy obok — 14 px / 21 px */}
        <p className="text-body-s text-fg-secondary">{summary.summary}</p>
        {summary.openQuestions.length > 0 && (
          <div className="mt-2">
            <p className="text-caption font-medium text-fg-muted">Do wyjaśnienia</p>
            <ul className="mt-1 list-disc pl-5 text-body-s text-fg-secondary">
              {summary.openQuestions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </div>
        )}
        <CopilotFootnote
          className="mt-2"
          kind={summary.noData ? 'Sprawdzono' : 'Podsumowanie AI'}
          meta={summary.meta}
          sources={summary.sources}
        />
      </>
    );
  } else if (!enabled) {
    body = <p className="text-body-s text-fg-muted">AI jest wyłączone. Podsumowanie pojawi się po włączeniu AI.</p>;
  } else {
    body = (
      <p className="text-body-s text-fg-muted">
        Brak podsumowania. AI zbierze najważniejsze sprawy klienta w 2–3 zdaniach, ze źródłami.
      </p>
    );
  }

  return (
    <Section aria-labelledby={`w-skrocie-${clientId}`} className={className}>
      <SectionHeader tone="strong" title={<span id={`w-skrocie-${clientId}`}>W skrócie</span>} action={action} />
      {body}
      {error && (
        <div className="mt-2">
          <FormMessage>{error.message}</FormMessage>
        </div>
      )}
    </Section>
  );
}
